// School-timezone-pinned date helpers. The backend stores dates such as
// `tests.scheduled_date` as a Postgres DATE (calendar date, no TZ); the pg
// driver, running in the school's timezone, serialises that into JSON as the
// UTC instant of local midnight (e.g. "…T18:30:00.000Z" for a UTC+5:30 school).
// Anything that then does `.slice(0, 10)` reads the UTC date portion and
// shows the wrong day.
//
// Relying on the device's local timezone is not enough either: some Android
// WebViews ship locale data that ignores the device tz and emit the UTC
// portion of the timestamp. So every formatter and every date accessor is
// pinned to the configured school timezone (VITE_TIMEZONE, see src/brand.ts),
// keeping the calendar day consistent end-to-end.

import { brand } from "../src/brand";

const IST = brand.timezone;

// "YYYY-MM-DD" in the school tz regardless of device tz. en-CA's date format is
// always YYYY-MM-DD, so this is also safe to feed to <input type="date">.
function istCalendarDate(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    timeZone: IST,
  }).format(d);
}

// "27 May 2026" — school-tz calendar date, human-friendly. Returns "" for
// nullish/invalid input so callers can short-circuit with `&&`.
export function formatLocalDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(brand.locale, {
    day: "numeric", month: "short", year: "numeric",
    timeZone: IST,
  });
}

// "2026-05-27" — what an <input type="date"> wants. school-tz calendar date, so
// editing a 2026-05-27 test pre-selects 2026-05-27 on every device
// regardless of its local tz or WebView locale.
export function localDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return istCalendarDate(d);
}

// Today's calendar date in IST as "YYYY-MM-DD". Used by callers that need
// to bucket "live vs upcoming" without relying on device-tz `new Date()`
// components.
export function istTodayKey(): string {
  return istCalendarDate(new Date());
}

// "YYYY-MM-DD" key for an ISO timestamp in IST. String-lex order matches
// calendar order, so callers compare with `<`/`>` directly. Returns null
// for nullish/invalid input.
export function istDayKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return istCalendarDate(d);
}

// "3d 11h" / "23h 5m" / "12m" / null. Null means either invalid input or
// the deadline has passed (the chip caller hides itself then). Reads the
// difference live each call — wrap in a setInterval to tick.
export function formatCountdown(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (isNaN(target)) return null;
  const ms = target - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / (24 * 3600_000));
  const h = Math.floor((ms % (24 * 3600_000)) / 3600_000);
  const m = Math.floor((ms % 3600_000) / 60_000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// True when the (chip-call) result should adopt the urgent (amber/red) tint
// — within the last hour before the deadline.
export function isCountdownUrgent(iso: string | null | undefined, now = Date.now()): boolean {
  if (!iso) return false;
  const target = new Date(iso).getTime();
  if (isNaN(target)) return false;
  const ms = target - now;
  return ms > 0 && ms < 60 * 60_000;
}
